import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — Search
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * An animated, expandable inline search component.
 * Collapses to an icon when empty and un-focused.
 * Expands into a search bar when clicked.
 * 
 * Usage:
 *   import { Search } from '@/core/oblique-ui'
 *   
 *   <Search 
 *      value={query} 
 *      onChange={setQuery} 
 *      placeholder="Search..." 
 *      expandedWidth={260}
 *   />
 */

export function Search({ 
    value = '', 
    onChange, 
    onCommand, 
    placeholder = 'Search...', 
    expandedWidth = 260,
    className = '' 
}) {
    const [isExpanded, setIsExpanded] = useState(Boolean(value));
    const inputRef = useRef(null);

    // Auto-focus when expanded
    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    // Keep expanded if there's a value
    useEffect(() => {
        if (value) {
            setIsExpanded(true);
        }
    }, [value]);

    return (
        <div className={`relative flex items-center ${className}`}>
            <AnimatePresence initial={false}>
                {isExpanded ? (
                    <motion.div
                        key="expanded"
                        initial={{ width: 36, opacity: 0, scale: 0.95 }}
                        animate={{ width: expandedWidth, opacity: 1, scale: 1 }}
                        exit={{ width: 36, opacity: 0, scale: 0.95 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="h-8 bg-[var(--bg-subtle)] border border-[var(--border)] rounded-full flex items-center px-2.5 gap-2"
                    >
                        <ObliqueIcon name="search" size={15} color="var(--text-tertiary)" />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder={placeholder}
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && value.startsWith('/') && onCommand) {
                                    onCommand(value);
                                    onChange('');
                                    setIsExpanded(false);
                                }
                            }}
                            onBlur={() => {
                                if (!value) setIsExpanded(false);
                            }}
                            className="flex-1 !bg-transparent [background:transparent!important] !border-none [border:none!important] !outline-none [outline:none!important] text-[13px] font-medium text-[var(--text-primary)] !p-0 [padding:0!important] !m-0 [margin:0!important] shadow-none focus:ring-0 placeholder:text-[var(--text-tertiary)]"
                        />
                        {value && (
                            <button
                                onClick={() => {
                                    onChange('');
                                    if (inputRef.current) inputRef.current.focus();
                                }}
                                className="bg-transparent border-none p-0.5 rounded-full cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)] transition-colors flex items-center justify-center shrink-0"
                            >
                                <ObliqueIcon name="close" size={13} />
                            </button>
                        )}
                    </motion.div>
                ) : (
                    <motion.button
                        key="collapsed"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.1 }}
                        onClick={() => setIsExpanded(true)}
                        className="w-8 h-8 rounded-full bg-transparent border-none flex items-center justify-center cursor-pointer text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors p-0"
                        title="Search"
                    >
                        <ObliqueIcon name="search" size={18} />
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    );
}
